import './UserDropdown.scss';
import { useTranslator } from '@Localization';
import { Dropdown, IconButton, ListDivider, Menu, MenuButton, MenuItem } from '@mui/joy';
import { useConfiguration } from 'common/hooks/useConfiguration';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useJobPermissions } from '../../modules/jobs/jobPermissions';
import { EnableFor } from '../containers/EnableForRole';
import { ACDLIo } from '../routes/config/ExternalLinks';
import { Home } from '../routes/config/Home';
import { ConfirmationModal } from './ConfirmationModal';
import { DropdownExternalLinkItem } from './DropdownExternalLinkItem';
import { DropdownLinkItem } from './DropdownLinkItem';
import { UserAvatar } from './UserAvatar';

interface IUserDropdownProps {
  shortName: string;
  showModal: boolean;
  workspaceId: string;
  onLogout: () => Promise<void>;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

export const UserDropdown = observer((props: IUserDropdownProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useJobPermissions();
  const config = useConfiguration();

  return (
    <div className='top-nav-item'>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: 'topbar' } }}
          id='user-dropdown'
        >
          <UserAvatar shortName={props.shortName} />
        </MenuButton>
        <Menu
          className='user-dropdown-menu'
          placement='bottom-end'
        >
          <DropdownLinkItem
            id='user-dropdown-account-button'
            linkTo={Home.User.Account.withParams({ workspaceId: props.workspaceId })}
          >
            {t('account')}
          </DropdownLinkItem>
          {!config.appConfig.runLocally && (
            <EnableFor
              disableForEncryption
              hasPermission={permissions.canReadCostList(true)}
            >
              <DropdownLinkItem
                id='user-dropdown-cloud-costs-button'
                linkTo={Home.User.CloudCosts.History.withParams({ workspaceId: props.workspaceId })}
              >
                {t('cloud_costs_label')}
              </DropdownLinkItem>
            </EnableFor>
          )}
          <ListDivider />
          <DropdownExternalLinkItem
            id='user-dropdown-tutorials-button'
            linkTo={ACDLIo.Tutorials.Path}
          >
            {t('manual')}
          </DropdownExternalLinkItem>
          <ListDivider />
          <MenuItem
            id='user-dropdown-logout-button'
            onClick={() => {
              void props.onLogout();
            }}
          >
            {t('log_out')}
          </MenuItem>
        </Menu>
      </Dropdown>
      <ConfirmationModal
        showModal={props.showModal}
        onCancel={props.onCancel}
        onConfirm={props.onConfirm}
        confirmationHeader='user-logout-confirmation-header'
        confirmationQuestion='user-logout-confirmation-question'
      />
    </div>
  );
});
